// @file Composable that enables elements in the whiteboard content picker to be dragged and dropped.
import { trackEvent } from '@@/bits/analytics'
import { ContentPickerSource, CONTENT_SOURCE_BY_SEARCH_MODE } from '@@/bits/content_picker'
import device from '@@/bits/device'
import { __ } from '@@/bits/intl'
import type {
  AudioSearchResult,
  ImageSearchResult,
  SearchFilters,
  VideoSearchResult,
  WebSearchResult,
} from '@@/bits/web_search'
import { createStickerShape, STICKER_SIZE } from '@@/bits/whiteboard_upload'
import { useContentPickerStore } from '@@/pinia/content_picker'
import { useRecentEmojisStore } from '@@/pinia/recent_emojis_store'
import { useWhiteboardStore } from '@@/pinia/whiteboard'
import { useWhiteboardPermissionsStore } from '@@/pinia/whiteboard_permissions'
import { useWhiteboardToolbarStore } from '@@/pinia/whiteboard_toolbar'
import { useWhiteboardUploadStore } from '@@/pinia/whiteboard_upload'
import type { DragData } from '@@/pragmatic-drag-and-drop/helpers'
import { useDraggableElement } from '@@/pragmatic-drag-and-drop/useDraggableElement'
import { useTldrawEditor } from '@@/vuecomposables/useTldrawEditor'
import type { CleanupFn } from '@atlaskit/pragmatic-drag-and-drop/dist/types/internal-types'
import { dropTargetForElements } from '@atlaskit/pragmatic-drag-and-drop/element/adapter'
import { preventUnhandled } from '@atlaskit/pragmatic-drag-and-drop/prevent-unhandled'
import type { Emoji } from '@padlet/arvo'
import type { TLPageId } from 'tldraw'
import { computed, onBeforeUnmount, onMounted, ref, type Ref } from 'vue'

const DRAGGABLE_IMAGE_ELEMENT_TYPE = 'whiteboard_image_card'
const DRAGGABLE_AV_ELEMENT_TYPE = 'whiteboard_av_card'
const DRAGGABLE_WEB_ELEMENT_TYPE = 'whiteboard_web_card'
const DRAGGABLE_STICKER_ELEMENT_TYPE = 'whiteboard_sticker'

interface ImageDragData extends DragData {
  result: ImageSearchResult
  searchFilter: SearchFilters
}

interface AudioVideoDragData extends DragData {
  result: AudioSearchResult | VideoSearchResult
  searchFilter: SearchFilters
}

interface WebDragData extends DragData {
  result: WebSearchResult
  searchFilter: typeof SearchFilters.Web
}

interface StickerDragData extends DragData {
  result: Emoji
  searchFilter: typeof SearchFilters.Stickers
}

type DragType =
  | typeof DRAGGABLE_IMAGE_ELEMENT_TYPE
  | typeof DRAGGABLE_AV_ELEMENT_TYPE
  | typeof DRAGGABLE_WEB_ELEMENT_TYPE
  | typeof DRAGGABLE_STICKER_ELEMENT_TYPE

type GetDragDataByType<T extends DragType> = T extends typeof DRAGGABLE_IMAGE_ELEMENT_TYPE
  ? ImageDragData
  : T extends typeof DRAGGABLE_AV_ELEMENT_TYPE
  ? AudioVideoDragData
  : T extends typeof DRAGGABLE_WEB_ELEMENT_TYPE
  ? WebDragData
  : T extends typeof DRAGGABLE_STICKER_ELEMENT_TYPE
  ? StickerDragData
  : never

interface UseWhiteboardContentPickerDraggableElementOptions<T extends DragType> {
  elementRef: Ref<HTMLElement | undefined>
  type: T
  data: Ref<GetDragDataByType<T>>
  dragPreview?: Ref<HTMLElement | undefined>
  isDraggable?: Ref<boolean>
}

function useWhiteboardContentPickerDraggableElement<T extends DragType>(
  options: UseWhiteboardContentPickerDraggableElementOptions<T>,
): {
  isDragging: Ref<boolean>
} {
  const whiteboardToolbarStore = useWhiteboardToolbarStore()
  const whiteboardPermissionsStore = useWhiteboardPermissionsStore()
  const whiteboardStore = useWhiteboardStore()

  const { elementRef, type, data, dragPreview, isDraggable = computed(() => true) } = options

  const dragStartPosition = ref<{ x: number; y: number } | null>(null)
  const hasExceededDistance = ref(false)

  // Set custom drop message based on dragged element type
  const setDropMessageForType = (elementType: DragType): void => {
    switch (elementType) {
      case DRAGGABLE_IMAGE_ELEMENT_TYPE:
        whiteboardToolbarStore.setDropOverlayMessage(__('Drop image anywhere on the card'))
        break
      case DRAGGABLE_AV_ELEMENT_TYPE:
        whiteboardToolbarStore.setDropOverlayMessage(__('Drop media anywhere on the card'))
        break
      case DRAGGABLE_WEB_ELEMENT_TYPE:
        whiteboardToolbarStore.setDropOverlayMessage(__('Drop web content anywhere on the card'))
        break
      case DRAGGABLE_STICKER_ELEMENT_TYPE:
        whiteboardToolbarStore.setDropOverlayMessage(__('Drop sticker anywhere on the card'))
        break
    }
  }

  const { isDragging } = useDraggableElement<GetDragDataByType<T>, any>({
    elementRef,
    type,
    data,
    dragPreview,
    isDraggable: computed(() => {
      return (
        !device.phone &&
        isDraggable.value &&
        whiteboardPermissionsStore.canIWriteFrame(whiteboardStore.currentFrameId as TLPageId)
      )
    }),
    onDragStart: (e) => {
      preventUnhandled.start()
      dragStartPosition.value = {
        x: e.location.current.input.clientX,
        y: e.location.current.input.clientY,
      }
      hasExceededDistance.value = false
      setDropMessageForType(type)
    },
    onDrag: (e) => {
      if (!dragStartPosition.value) return

      const currentX = e.location.current.input.clientX
      const currentY = e.location.current.input.clientY
      const startX = dragStartPosition.value.x
      const startY = dragStartPosition.value.y
      const distance = Math.sqrt(Math.pow(currentX - startX, 2) + Math.pow(currentY - startY, 2))

      if (distance > 40 && !hasExceededDistance.value) {
        hasExceededDistance.value = true
        whiteboardToolbarStore.hideToolbarMenu()
      }
    },
    onDrop: () => {
      hasExceededDistance.value = false
    },
  })

  return {
    isDragging,
  }
}

const useWhiteboardContentPickerDropTargetForElements = (elementRef: Ref<HTMLElement | undefined>): void => {
  const whiteboardUploadStore = useWhiteboardUploadStore()
  const whiteboardPermissionsStore = useWhiteboardPermissionsStore()
  const whiteboardStore = useWhiteboardStore()
  const contentPickerStore = useContentPickerStore()
  const recentEmojisStore = useRecentEmojisStore()

  let elementsCleanup: CleanupFn | undefined
  onMounted(() => {
    if (device.phone) return

    elementsCleanup = dropTargetForElements({
      element: elementRef.value as HTMLElement,
      canDrop: (args) => {
        if (!whiteboardPermissionsStore.canIWriteFrame(whiteboardStore.currentFrameId as TLPageId)) return false
        const draggableSourceType = args.source.data.type as DragType
        return [
          DRAGGABLE_IMAGE_ELEMENT_TYPE,
          DRAGGABLE_AV_ELEMENT_TYPE,
          DRAGGABLE_WEB_ELEMENT_TYPE,
          DRAGGABLE_STICKER_ELEMENT_TYPE,
        ].includes(draggableSourceType)
      },
      onDrop: (args) => {
        preventUnhandled.stop()

        const editor = useTldrawEditor().editor
        if (!editor) return

        // Extract data from the dragged element
        const dragData = args.source.data.data as
          | ImageDragData
          | AudioVideoDragData
          | WebDragData
          | StickerDragData
          | undefined
        const result = dragData?.result
        const searchFilter = dragData?.searchFilter
        const location = args.location.current.input

        if (result == null || searchFilter == null || location == null) return

        // Convert screen coordinates to tldraw coordinates
        const dropPosition = editor.screenToPage({
          x: location.clientX,
          y: location.clientY,
        })

        const url = result.url
        if (typeof url !== 'string') return

        // Get content source from search filter
        const contentSource = CONTENT_SOURCE_BY_SEARCH_MODE[searchFilter]
        if (contentSource == null) return

        if (contentSource === ContentPickerSource.Stickers) {
          const emoji = result as Emoji

          // Offset the position so the center of the sticker is at the drop point
          const centeredX = dropPosition.x - STICKER_SIZE / 2
          const centeredY = dropPosition.y - STICKER_SIZE / 2
          createStickerShape({
            url,
            x: centeredX,
            y: centeredY,
          })
          recentEmojisStore.addToRecents(emoji)

          trackEvent('Emoji Picker', 'Picked emoji', emoji.name, undefined, {
            category: emoji.category?.label,
            source: 'dragAndDrop',
            trackingContext: 'EmojiPicker',
          })
        } else {
          whiteboardUploadStore.startNewUrlUploadFromSearchResult(url, {
            searchFilter,
            contentSource,
            altText: 'title' in result ? result.title : undefined,
            thumbnailWidth: 'thumbnail_width' in result ? Number(result.thumbnail_width) : undefined,
            thumbnailHeight: 'thumbnail_height' in result ? Number(result.thumbnail_height) : undefined,
            thumbnailUrl: 'thumbnail_url' in result ? result.thumbnail_url : undefined,
            dropPosition,
          })

          trackEvent('Content Picker', 'Dropped search result', null, null, {
            contentSource,
          })
        }

        contentPickerStore.resetContentPicker()
      },
    })
  })

  onBeforeUnmount(() => {
    elementsCleanup?.()
  })
}

export {
  DRAGGABLE_AV_ELEMENT_TYPE,
  DRAGGABLE_IMAGE_ELEMENT_TYPE,
  DRAGGABLE_STICKER_ELEMENT_TYPE,
  DRAGGABLE_WEB_ELEMENT_TYPE,
  useWhiteboardContentPickerDraggableElement,
  useWhiteboardContentPickerDropTargetForElements,
}
